/*
 * Copyright (c) 2015-2025. AX Enterprize, LLC
 * All rights reserved.
 */

import {BaseModel, BaseModelDeserializer} from '..';
import {DateTime} from 'luxon';
import {fail, isEmpty, isObject, Result, some} from '../../utils';

export interface TStadiumEvent {
  GAME_DETAIL_ID?: string;
  EVENT_NAME?: string;
  VENUE?: string;
  GAME_DATE?: number;
  END_DATE?: number;
  STATUS?: string;
  LEAGUE_NAME?: string;
  IS_ACTIVE?: number;
  latitude?: string;
  longitude?: string;
  updatedAt?: string;
  TIME_REMAINING?: string;
}

export class StadiumEvent implements BaseModel {
  gameDetailId?: string;
  eventName?: string;
  venue?: string;
  gameDate?: DateTime;
  endDate?: DateTime;
  status?: string;
  leagueName?: string;
  isActive?: boolean;
  latitude?: string;
  longitude?: string;
  updatedAt?: DateTime;
  timeRemaining?: string;

  constructor(values: {
    gameDetailId?: string;
    eventName?: string;
    venue?: string;
    gameDate?: DateTime;
    endDate?: DateTime;
    status?: string;
    leagueName?: string;
    isActive?: boolean;
    latitude?: string;
    longitude?: string;
    updatedAt?: DateTime;
    timeRemaining?: string;
  }) {
    this.gameDetailId = values.gameDetailId;
    this.eventName = values.eventName;
    this.venue = values.venue;
    this.gameDate = values.gameDate;
    this.endDate = values.endDate;
    this.status = values.status;
    this.leagueName = values.leagueName;
    this.isActive = values.isActive;
    this.latitude = values.latitude;
    this.longitude = values.longitude;
    this.updatedAt = values.updatedAt;
    this.timeRemaining = values.timeRemaining;
  }
}

export class StadiumEventUtil implements BaseModelDeserializer<TStadiumEvent, StadiumEvent> {
  deserialize(raw: unknown): Result<StadiumEvent> {
    if (isEmpty(raw)) return fail('No data supplied for stadium event');
    if (!isObject(raw)) return fail('Invalid data supplied for stadium event');

    let gameDetailId: string | undefined = undefined;
    if (('GAME_DETAIL_ID' in raw) && !isEmpty(raw.GAME_DETAIL_ID)) {
      if (typeof raw.GAME_DETAIL_ID !== 'string') return fail('Invalid game detail ID value');
      gameDetailId = raw.GAME_DETAIL_ID;
    }

    let eventName: string | undefined = undefined;
    if (('EVENT_NAME' in raw) && !isEmpty(raw.EVENT_NAME)) {
      if (typeof raw.EVENT_NAME !== 'string') return fail('Invalid event name value');
      eventName = raw.EVENT_NAME;
    }

    let venue: string | undefined = undefined;
    if (('VENUE' in raw) && !isEmpty(raw.VENUE)) {
      if (typeof raw.VENUE !== 'string') return fail('Invalid venue value');
      venue = raw.VENUE;
    }

    let gameDate: DateTime | undefined = undefined;
    if (('GAME_DATE' in raw) && !isEmpty(raw.GAME_DATE)) {
      if (typeof raw.GAME_DATE !== 'number') return fail('Invalid game date value');
      gameDate = DateTime.fromMillis(raw.GAME_DATE);
      if (!gameDate.isValid) return fail('Invalid game date value');
    }

    let endDate: DateTime | undefined = undefined;
    if (('END_DATE' in raw) && !isEmpty(raw.END_DATE)) {
      if (typeof raw.END_DATE !== 'number') return fail('Invalid end date value');
      endDate = DateTime.fromMillis(raw.END_DATE);
      if (!endDate.isValid) return fail('Invalid end date value');
    }

    let status: string | undefined = undefined;
    if (('STATUS' in raw) && !isEmpty(raw.STATUS)) {
      if (typeof raw.STATUS !== 'string') return fail('Invalid status value');
      status = raw.STATUS;
    }

    let leagueName: string | undefined = undefined;
    if (('LEAGUE_NAME' in raw) && !isEmpty(raw.LEAGUE_NAME)) {
      if (typeof raw.LEAGUE_NAME !== 'string') return fail('Invalid league name value');
      leagueName = raw.LEAGUE_NAME;
    }

    let isActive: boolean | undefined = undefined;
    if (('IS_ACTIVE' in raw) && !isEmpty(raw.IS_ACTIVE)) {
      if (typeof raw.IS_ACTIVE !== 'number') return fail('Invalid active status value');
      isActive = raw.IS_ACTIVE === 1;
    }

    let latitude: string | undefined = undefined;
    if (('latitude' in raw) && !isEmpty(raw.latitude)) {
      if (typeof raw.latitude !== 'string') return fail('Invalid latitude value');
      latitude = raw.latitude;
    }

    let longitude: string | undefined = undefined;
    if (('longitude' in raw) && !isEmpty(raw.longitude)) {
      if (typeof raw.longitude !== 'string') return fail('Invalid longitude value');
      longitude = raw.longitude;
    }

    let updatedAt: DateTime | undefined = undefined;
    if (('updatedAt' in raw) && !isEmpty(raw.updatedAt)) {
      if (typeof raw.updatedAt !== 'number') return fail('Invalid updated at value');
      updatedAt = DateTime.fromMillis(raw.updatedAt);
      if (!updatedAt.isValid) return fail('Invalid updated at value');
    }

    let timeRemaining: string | undefined = undefined;
    if (('TIME_REMAINING' in raw) && !isEmpty(raw.TIME_REMAINING)) {
      if (typeof raw.TIME_REMAINING !== 'string') return fail('Invalid time remaining value');
      timeRemaining = raw.TIME_REMAINING;
    }

    return some(new StadiumEvent({
      gameDetailId: gameDetailId,
      eventName: eventName,
      venue: venue,
      gameDate: gameDate,
      endDate: endDate,
      status: status,
      leagueName: leagueName,
      isActive: isActive,
      latitude: latitude,
      longitude: longitude,
      updatedAt: updatedAt,
      timeRemaining: timeRemaining
    }));
  }

  deserializeArray(raw: unknown): Result<StadiumEvent[]> {
    if (!Array.isArray(raw)) return fail('Invalid type for stadium event array');
    const results: StadiumEvent[] = [];
    let error = '';
    raw.some((val: any) => {
      let result = this.deserialize(val);
      if (result.type === 'error') {
        error = result.message;
        return true;
      } else {
        results.push(result.value);
        return false;
      }
    });
    return error ? fail(error) : some(results);
  }
}

export const DEFAULT_STADIUM_EVENT_UTIL = new StadiumEventUtil()
